import {
  FeatherCreditCard,
  FeatherEdit3,
  FeatherFileText,
  FeatherReceipt,
} from "@subframe/core";
import type { AccountantStatusSummaryEntity } from "../../../domain/entities/AccountantStatusEntity";

interface AccountantSummaryCardsSectionProps {
  summary: AccountantStatusSummaryEntity | null;
  loading: boolean;
  error: string | null;
}

function Card({
  title,
  icon,
  primary,
  secondary,
  caption,
}: {
  title: string;
  icon: React.ReactNode;
  primary: string;
  secondary: string;
  caption: string;
}) {
  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {title}
        </span>
        {icon}
      </div>
      <div className="flex w-full flex-col items-start gap-2">
        <span className="text-heading-2 font-heading-2 text-default-font">
          {primary}
        </span>
        <span className="text-body font-body text-warning-600">
          {secondary}
        </span>
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {caption}
      </span>
    </div>
  );
}

export function AccountantSummaryCardsSection({
  summary,
  loading,
  error,
}: AccountantSummaryCardsSectionProps) {
  if (loading) {
    return (
      <div className="flex w-full flex-wrap items-start gap-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="flex h-48 grow shrink-0 basis-0 animate-pulse rounded-md bg-neutral-100"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full rounded-md border border-solid border-error-200 bg-error-50 px-6 py-4">
        <span className="text-body font-body text-error-700">{error}</span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-wrap items-start gap-4">
      <Card
        title="Customer Invoices"
        icon={
          <FeatherReceipt className="text-body font-body text-success-600" />
        }
        primary={`${summary?.customerInvoices.processed ?? 0} processed`}
        secondary={`${summary?.customerInvoices.pending ?? 0} pending`}
        caption="Invoices created and updated for this period."
      />
      <Card
        title="Vendor Bills"
        icon={
          <FeatherFileText className="text-body font-body text-brand-600" />
        }
        primary={`${summary?.vendorBills.processed ?? 0} processed`}
        secondary={`${summary?.vendorBills.pending ?? 0} pending`}
        caption="Bills recorded and ready in your books."
      />

      <Card
        title="Bank Transactions"
        icon={
          <FeatherCreditCard className="text-body font-body text-brand-600" />
        }
        primary={`${summary?.bankTransactions.categorized ?? 0} categorized`}
        secondary={`${summary?.bankTransactions.needsReview ?? 0} need review`}
        caption="Fetched from bank feeds and categorized."
      />
    </div>
  );
}
